import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * The warned-reset confirmation (Outlook semantics): rescheduling a series
 * discards its per-occurrence changes. SERVER-authoritative — the counts
 * and the retry challenge come from the 409 itself, so what the user reads
 * is exactly the state that will be discarded (the server re-verifies that
 * state under lock on the confirmed retry; if anything changed in between,
 * this dialog re-opens with fresh counts rather than deleting more than
 * was shown).
 */
export interface ConfirmDiscardExceptionsDialogProps {
  open: boolean;
  changedCount: number;
  deletedCount: number;
  saving?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function pieces(changedCount: number, deletedCount: number): string {
  const parts: string[] = [];
  if (changedCount > 0) {
    parts.push(`${changedCount} changed event${changedCount === 1 ? '' : 's'}`);
  }
  if (deletedCount > 0) {
    parts.push(`${deletedCount} deleted event${deletedCount === 1 ? '' : 's'}`);
  }
  return parts.join(' and ');
}

export function ConfirmDiscardExceptionsDialog({
  open,
  changedCount,
  deletedCount,
  saving,
  onConfirm,
  onCancel,
}: ConfirmDiscardExceptionsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent role="alertdialog" aria-describedby="discard-exceptions-description">
        <DialogHeader>
          <DialogTitle>Reschedule repeating event</DialogTitle>
          <DialogDescription id="discard-exceptions-description">
            This series has {pieces(changedCount, deletedCount)}. Changes made to individual
            events in this series will be discarded, and deleted events will be restored —
            including in your connected calendars, which may take a short time to sync.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={saving} autoFocus>
            Discard and reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
